<template>
  <v-container fluid>
    <v-dialog
      v-model="createDuplicatesDialog"
      max-width="300px"
      height="400px"
      scrollable
    >
      <v-card>
        <v-card-title>
          How many identical items should be created?
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="duplicateCount"
            :items="duplicateCountOptions"
            label="Quantity"
          />
          <v-btn
            text
            @click="createDuplicatesDialog = false"
          >
            Close
          </v-btn>
          <v-btn
            color="primary"
            :disabled="duplicateCount === 0"
            :loading="creatingDuplicates"
            @click="createDuplicates"
          >
            Create {{ duplicateCount }} duplicates
          </v-btn>
        </v-card-text>
        <v-card-text v-if="createdDuplicates.length > 0">
          <v-list>
            <v-list-item
              v-for="(item, i) in createdDuplicates"
              :key="i"
            >
              <v-list-item-content>
                <v-list-item-title>
                  Item <a :href="'/inventory/' + item.itemId">{{ item.icn }}</a>
                  has been created
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-stepper
      v-model="currentStep"
      vertical
    >
      <v-stepper-step
        :complete="currentStep > steps.date"
        step="1"
      >
        <div v-html="dateStepName" />
      </v-stepper-step>

      <v-stepper-content :step="steps.date">
        <v-menu
          ref="menu"
          v-model="menu"
          max-width="290"
          :close-on-content-click="false"
          :return-value.sync="date"
          transition="slide-x-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="date"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="date"
            width="290"
            scrollable
            :max="new Date().toISOString().substr(0, 10)"
          >
            <v-spacer />
            <v-btn
              color="primary"
              @click="$refs.menu.save(date)"
            >
              OK
            </v-btn>
            <v-btn
              text
              @click="menu = false"
            >
              Cancel
            </v-btn>
          </v-date-picker>
        </v-menu>
        <v-btn
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :complete="currentStep > steps.clientSelect"
        :step="steps.clientSelect"
      >
        <div v-html="clientStepName" />
      </v-stepper-step>

      <v-stepper-content :step="steps.clientSelect">
        <client-auto-complete
          :title="false"
          @onClickClient="clientSelected"
        />
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          :disabled="isClientSelected"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :complete="currentStep > steps.subClientSelect"
        :step="steps.subClientSelect"
      >
        <div v-html="subClientStepName" />
      </v-stepper-step>

      <v-stepper-content :step="steps.subClientSelect">
        <client-auto-complete
          :title="false"
          :subclient="true"
          @onClickClient="subClientSelected"
        />
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="isSubClientSelected"
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.manufacturerSelect"
        :complete="currentStep > steps.manufacturerSelect"
      >
        <div v-html="manufacturerStepName" />
      </v-stepper-step>
      <v-stepper-content :step="steps.manufacturerSelect">
        <manufacturer-auto-complete
          :key="rerenderMfgAutocomplete"
          :title="false"
          @onClickMfg="mfgSelected"
        />
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="isMfgSelected"
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.description"
        :complete="currentStep > steps.description"
      >
        <div v-html="descriptionStepName" />
      </v-stepper-step>
      <v-stepper-content :step="steps.description">
        <v-textarea
          v-model="desc"
          outlined
          counter
          clearable
          label="Item Description"
          clear-icon="mdi-close"
          :rules="[rules.required, rules.lengthSixtyFour]"
          no-resize
        />
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="hasDescription"
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.size"
        :complete="currentStep > steps.size"
      >
        <div v-html="sizeStepName" />
      </v-stepper-step>
      <v-stepper-content :step="steps.size">
        <v-container>
          <v-row>
            <v-text-field
              v-model="width"
              type="number"
              label="Width (in inches)"
            />
          </v-row>
          <v-row>
            <v-text-field
              v-model="length"
              type="number"
              label="Length (in inches)"
            />
          </v-row>
          <v-row>
            <v-text-field
              v-model="height"
              type="number"
              label="Height (in inches)"
            />
          </v-row>
          <v-row>
            <v-text-field
              v-model="cubicFeet"
              label="Size in cubic feet"
              readonly
              suffix=""
            />
          </v-row>
        </v-container>
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.palletDisposal"
        :complete="currentStep > steps.palletDisposal"
      >
        Pallet/Crate Disposal
      </v-stepper-step>
      <v-stepper-content :step="steps.palletDisposal">
        <v-container>
          <v-row>
            Did this shipment arrive on pallet(s)/crate(s) that must be broken down/demolished?
          </v-row>
          <v-row>
            <v-btn
              align="left"
              text
              ripple
              color="blue darken-1"
              @click="palletDisposal = true"
            >
              Yes
            </v-btn>
            <v-btn
              align="right"
              text
              ripple
              color="blue darken-1"
              @click="currentStep = steps.additionalServices"
            >
              No
            </v-btn>
          </v-row>
          <v-row v-if="palletDisposal">
            <v-select
              v-model="palletDisposalCount"
              :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
              label="How many pallets/crates require disposal?"
            />
          </v-row>
        </v-container>
        <v-btn
          text
          @click="resetPalletDisposal"
        >
          Back
        </v-btn>
        <v-btn
          v-if="palletDisposal"
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.additionalServices"
        :complete="currentStep > steps.additionalServices"
      >
        <div v-html="additionalServicesStepName" />
      </v-stepper-step>
      <v-stepper-content :step="steps.additionalServices">
        <v-container>
          <v-row v-if="!addlServices">
            Are additional warehouse services necessary at the time of receiving?
          </v-row>
          <v-row v-if="!addlServices">
            <v-btn
              align="left"
              text
              ripple
              color="blue darken-1"
              @click="addlServices = true"
            >
              Yes
            </v-btn>
            <v-btn
              align="right"
              text
              ripple
              color="blue darken-1"
              @click="currentStep = steps.additionalMaterials"
            >
              No
            </v-btn>
          </v-row>
          <v-row v-if="addlServices">
            <v-select
              v-model="selectedServices"
              :items="additionalServices"
              item-text="title"
              item-value="value"
              label="Select price of additional services"
            />
          </v-row>
          <v-row v-if="addlServices">
            <v-textarea
              v-model="addlServicesNotes"
              outlined
              counter
              clearable
              label="Notes"
              clear-icon="mdi-close"
              no-resize
            />
          </v-row>
        </v-container>
        <v-btn
          text
          @click="resetAdditionalServices"
        >
          Back
        </v-btn>
        <v-btn
          v-if="addlServices"
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>
      <v-stepper-step
        :step="steps.additionalMaterials"
        :complete="currentStep > steps.additionalMaterials"
      >
        <div v-html="additionalMaterialsStepName" />
      </v-stepper-step>

      <v-stepper-content :step="steps.additionalMaterials">
        <v-container>
          <v-row v-if="!addlMats">
            Are additional packing materials necessary at the time of receiving?
          </v-row>
          <v-row v-if="!addlMats">
            <v-btn
              align="left"
              text
              ripple
              color="blue darken-1"
              @click="addlMats = true"
            >
              Yes
            </v-btn>
            <v-btn
              align="right"
              text
              ripple
              color="blue darken-1"
              @click="currentStep = steps.generalNotes"
            >
              No
            </v-btn>
          </v-row>
          <v-row v-if="addlMats">
            <v-combobox
              v-model="selectedMaterials"
              :items="additionalMaterials"
              prepend-inner-icon="mdi-currency-usd"
              label="Select or enter additional materials"
            />
          </v-row>
          <v-row v-if="addlMats">
            <v-textarea
              v-model="addlMaterialsNotes"
              outlined
              counter
              clearable
              label="Notes"
              clear-icon="mdi-close"
              no-resize
            />
          </v-row>
        </v-container>
        <v-btn
          text
          @click="resetAdditionalMaterials"
        >
          Back
        </v-btn>
        <v-btn
          v-if="addlMats"
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>
      <v-stepper-step
        :step="steps.generalNotes"
        :complete="currentStep > steps.generalNotes"
      >
        <div v-html="generalNotesStepName" />
      </v-stepper-step>
      <v-stepper-content :step="steps.generalNotes">
        <v-container>
          <v-row v-if="!addGeneralNotes">
            Would you like to add any general notes?
          </v-row>
          <v-row v-if="!addGeneralNotes">
            <v-btn
              align="left"
              text
              ripple
              color="blue darken-1"
              @click="addGeneralNotes = true"
            >
              Yes
            </v-btn>
            <v-btn
              align="right"
              text
              ripple
              color="blue darken-1"
              @click="currentStep = steps.photos"
            >
              No
            </v-btn>
          </v-row>
          <v-row v-if="addGeneralNotes">
            <v-textarea
              v-model="generalNotes"
              outlined
              counter
              clearable
              label="General Notes"
              clear-icon="mdi-close"
              no-resize
              :rules="[rules.lengthTwoFifty]"
            />
          </v-row>
        </v-container>
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          v-if="generalNotes"
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.photos"
        :complete="currentStep > steps.photos"
      >
        Photos
      </v-stepper-step>
      <v-stepper-content :step="steps.photos">
        <v-file-input
          v-model="firstFile"
          chips
          show-size
          accept="image/*"
          placeholder="First photo"
          prepend-icon="mdi-camera"
        />
        <v-file-input
          v-model="secondFile"
          chips
          show-size
          accept="image/*"
          placeholder="Second photo"
          prepend-icon="mdi-camera"
        />
        <v-file-input
          v-model="thirdFile"
          chips
          show-size
          accept="image/*"
          placeholder="Third photo"
          prepend-icon="mdi-camera"
        />
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.billOfLading"
        :complete="currentStep > steps.billOfLading"
      >
        Bill of Lading
      </v-stepper-step>
      <v-stepper-content :step="steps.billOfLading">
        <v-file-input
          v-model="billOfLading"
          chips
          show-size
          accept="image/*"
          placeholder="Bill of Lading"
          prepend-icon="mdi-camera"
        />
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="nextStep"
        >
          Next
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.clientAttentionRequired"
        :complete="currentStep > steps.clientAttentionRequired"
      >
        Client Attention Required
      </v-stepper-step>
      <v-stepper-content :step="steps.clientAttentionRequired">
        <v-container>
          <v-row>
            Should the client be notified that this item requires their attention?
          </v-row>
          <v-row>
            <v-btn
              align="left"
              text
              ripple
              color="blue darken-1"
              @click="
                needsClientAttention();
                currentStep = steps.placement;
              "
            >
              Yes
            </v-btn>
            <v-btn
              align="right"
              text
              ripple
              color="blue darken-1"
              @click="
                currentStep = steps.placement;
              "
            >
              No
            </v-btn>
          </v-row>
          <v-row>
            <v-btn
              text
              @click="resetClientAttention"
            >
              Back
            </v-btn>
          </v-row>
        </v-container>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.placement"
        :complete="currentStep > steps.placement"
      >
        Warehouse &amp; Internal Location
      </v-stepper-step>
      <v-stepper-content :step="steps.placement">
        <warehouse-select-component
          :warehouse="warehouse"
          @onSetModel="warehouseSelected"
        />
        <warehouse-location-select-component
          :key="locationSelectorKey"
          :warehouse-id="warehouse ? warehouse.warehouseId : null"
          :location="warehouseLocation"
          :default-pending="true"
          @onSetModel="locationSelected"
        />
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="!warehouse"
          @click="reviewItem"
        >
          Continue
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        :step="steps.review"
        :complete="currentStep > steps.review"
      >
        Review
      </v-stepper-step>
      <v-stepper-content :step="steps.review">
        <p><strong>Warehouse:</strong> {{ warehouse ? warehouse.name : '' }}</p>
        <p>
          <strong>Internal Location:</strong>
          {{ warehouseLocation ? warehouseLocation.name : 'Pending' }}
        </p>
        <v-btn
          text
          @click="previousStep"
        >
          Back
        </v-btn>
        <v-btn
          v-if="!itemCreated"
          :disabled="currentStep > steps.review"
          color="primary"
          @click="createItem"
        >
          Create
        </v-btn>
      </v-stepper-content>
    </v-stepper>
    <v-dialog
      v-model="createItemDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title
          primary-title
          style="flex-direction: column;"
        >
          Item <a :href="'/inventory/' + item.itemId">{{ item.icn }}</a> has been created
        </v-card-title>
        <v-card-text>
          <v-row
            v-if="firstFile !== null"
            align-content="center"
            justify="center"
          >
            <v-col
              class="subtitle-1 text-center"
              cols="12"
            >
              Uploading first photo
            </v-col>
            <v-col
              cols="6"
              class="subtitle-1 text-center"
            >
              <v-progress-linear
                v-if="uploadingPhoto1 && !upload1Complete"
                indeterminate
                color="primary"
              />
              <v-icon
                v-if="upload1Complete"
                center
              >
                mdi-checkbox-marked-circle
              </v-icon>
            </v-col>
          </v-row>
          <v-row
            v-if="secondFile !== null"
            align-content="center"
            justify="center"
          >
            <v-col
              class="subtitle-1 text-center"
              cols="12"
            >
              Uploading second photo
            </v-col>
            <v-col
              cols="6"
              class="subtitle-1 text-center"
            >
              <v-progress-linear
                v-if="uploadingPhoto2 && !upload2Complete"
                indeterminate
                color="primary"
              />
              <v-icon
                v-if="upload2Complete"
                center
              >
                mdi-checkbox-marked-circle
              </v-icon>
            </v-col>
          </v-row>
          <v-row
            v-if="thirdFile !== null"
            align-content="center"
            justify="center"
          >
            <v-col
              class="subtitle-1 text-center"
              cols="12"
            >
              Uploading third photo
            </v-col>
            <v-col
              cols="6"
              class="subtitle-1 text-center"
            >
              <v-progress-linear
                v-if="uploadingPhoto3 && !upload3Complete"
                indeterminate
                color="primary"
              />
              <v-icon
                v-if="upload3Complete"
                center
              >
                mdi-checkbox-marked-circle
              </v-icon>
            </v-col>
          </v-row>
          <v-row
            v-if="billOfLading !== null"
            align-content="center"
            justify="center"
          >
            <v-col
              class="subtitle-1 text-center"
              cols="12"
            >
              Uploading Bill of Lading
            </v-col>
            <v-col
              cols="6"
              class="subtitle-1 text-center"
            >
              <v-progress-linear
                v-if="uploadingBillOfLading && !uploadBillOfLadingComplete"
                indeterminate
                color="primary"
              />
              <v-icon
                v-if="uploadBillOfLadingComplete"
                center
              >
                mdi-checkbox-marked-circle
              </v-icon>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions
          v-if="
            (upload1Complete || firstFile === null) &&
              (secondFile === null || upload2Complete) &&
              (thirdFile === null || upload3Complete)
          "
          class="justify-center"
        >
          <v-menu
            top
            origin="center center"
            transition="scale-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                color="primary"
                dark
                v-bind="attrs"
                v-on="on"
              >
                Actions
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="newItem">
                <v-list-item-title>Create a new item</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="newItemInSequence">
                <v-list-item-title>Create a new item in this ICN sequence</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="showCreateDuplicates">
                <v-list-item-title>
                  Create identical inventory in this ICN sequence
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import ClientAutoComplete from './ClientAutoComplete.vue';
import ManufacturerAutoComplete from './ManufacturerAutoComplete.vue';
import WarehouseSelectComponent from './WarehouseComponent.vue';
import WarehouseLocationSelectComponent from './WarehouseLocationComponent.vue';

export default {
  name: 'NewInventoryItemForm',
  components: {
    ClientAutoComplete,
    ManufacturerAutoComplete,
    WarehouseSelectComponent,
    WarehouseLocationSelectComponent,
  },
  data: () => ({
    rules: {
      required: (value) => !!value || 'Required',
      lengthSixtyFour: (value) => {
        if (value !== null && value !== undefined && value.length > 64) {
          return 'Max 64 characters';
        }

        return true;
      },
      lengthTwentyTwo: (value) => {
        if (value !== null && value !== undefined && value.length > 22) {
          return 'Max 22 characters';
        }

        return true;
      },
      lengthThirty: (value) => {
        if (value !== null && value !== undefined && value.length > 30) {
          return 'Max 30 characters';
        }

        return true;
      },
      lengthTwoFifty: (value) => {
        if (value !== null && value !== undefined && value.length > 250) {
          return 'Max 250 characters';
        }

        return true;
      },
    },
    firstFile: null,
    secondFile: null,
    thirdFile: null,
    billOfLading: null,
    invItem: null,
    createDuplicatesDialog: false,
    duplicateCountOptions: [],
    duplicateCount: 0,
    client: {},
    subClient: {},
    mfg: {},
    warehouse: null,
    warehouseLocation: null,
    locationSelectorKey: 0,
    date: new Date().toISOString().substr(0, 10),
    currentStep: 1,
    menu: false,
    rerenderMfgAutocomplete: 0,
    length: 1.0,
    height: 1.0,
    width: 1.0,
    desc: null,
    addlServices: false,
    creatingDuplicates: false,
    clientAttentionRequired: false,
    addGeneralNotes: false,
    generalNotes: '',
    selectedServices: null,
    additionalServices: [
      { title: '10 Minutes ($16.50)', value: 16.5 },
      { title: '15 Minutes ($24.75)', value: 24.75 },
      { title: '20 Minutes ($33.00)', value: 33.0 },
      { title: '30 Minutes ($49.50)', value: 49.5 },
      { title: '45 Minutes ($74.25)', value: 74.25 },
      { title: '1 Hour ($99.00)', value: 99.0 },
    ],
    selectedMaterials: 0,
    additionalMaterials: [5.0, 9.0, 14.0, 19.0, 24.0],
    sizeOptions: [],
    addlServicesNotes: '',
    addlMaterialsNotes: '',
    addlMats: false,
    showUploading: false,
    uploadingPhoto1: false,
    upload1Complete: false,
    uploadingPhoto2: false,
    upload2Complete: false,
    uploadingPhoto3: false,
    upload3Complete: false,
    uploadingBillOfLading: false,
    uploadBillOfLadingComplete: false,
    createItemDialog: false,
    item: {},
    createdDuplicates: [],
    itemCreated: false,
    palletDisposal: false,
    palletDisposalCount: 0,
    steps: {
      date: 1,
      clientSelect: 2,
      subClientSelect: 3,
      manufacturerSelect: 4,
      description: 5,
      size: 6,
      palletDisposal: 7,
      additionalServices: 8,
      additionalMaterials: 9,
      generalNotes: 10,
      photos: 11,
      billOfLading: 12,
      clientAttentionRequired: 13,
      placement: 14,
      review: 15,
    },
  }),
  computed: {
    isClientSelected() {
      return this.client.clientId === undefined;
    },
    isSubClientSelected() {
      return this.subClient.clientId === undefined;
    },
    isMfgSelected() {
      return this.mfg.manufacturerId === undefined;
    },
    hasDescription() {
      return (
        this.desc === null
        || this.desc === undefined
        || this.desc.length > 64
        || this.desc.length === 0
      );
    },
    reviewDisabled() {
      return this.currentStep > 10;
    },
    cubicFeet() {
      const exactCubicFeet = (this.length / 12) * (this.width / 12) * (this.height / 12);
      const roundedCubicFeet = (Math.round(exactCubicFeet * 4) / 4).toFixed(2);
      return roundedCubicFeet;
    },
    dateStepName() {
      let text = '<span>Select a Date</span>';

      if (this.date && this.currentStep > 1) {
        text += ` <span style="color: blue;"><strong>(${this.date})</strong></span>`;
      }

      return text;
    },
    clientStepName() {
      let text = '<span>Select a Client</span>';

      if (this.client && this.client.name && this.currentStep > 2) {
        text += ` <span style="color: blue;"><strong>(${this.client.name})</strong></span>`;
      }

      return text;
    },
    subClientStepName() {
      let text = '<span>Select a Sub-Client</span>';

      if (this.subClient && this.subClient.name && this.currentStep > 3) {
        text += ` <span style="color: blue;"><strong>(${this.subClient.name})</strong></span>`;
      }

      return text;
    },
    manufacturerStepName() {
      let text = '<span>Select a Manufacturer</span>';

      if (this.mfg && this.mfg.name && this.currentStep > 4) {
        text += ` <span style="color: blue;"><strong>(${this.mfg.name})</strong></span>`;
      }

      return text;
    },
    descriptionStepName() {
      let text = '<span>Description</span>';

      if (this.desc && this.currentStep > 5) {
        text += ` <span style="color: blue;"><strong>(${this.desc})</strong></span>`;
      }

      return text;
    },
    sizeStepName() {
      let text = '<span>Size</span>';

      if (this.width && this.length && this.height && this.currentStep > 6) {
        text += ` <span style="color: blue;"><strong>(${this.width} x ${this.length} x ${this.height})</strong></span>`;
      }

      return text;
    },
    additionalServicesStepName() {
      let text = '<span>Additional Services</span>';

      if (this.addlServices && this.selectedServices && this.currentStep > 7) {
        text += ` <span style="color: blue;"><strong>(${this.$currency(
          this.selectedServices,
        )})</strong></span>`;
      }

      return text;
    },
    additionalMaterialsStepName() {
      let text = '<span>Additional Materials</span>';

      if (this.selectedMaterials > 0 && this.currentStep > 8) {
        text += ` <span style="color: blue;"><strong>(${this.$currency(
          this.selectedMaterials,
        )})</strong></span>`;
      }

      return text;
    },
    generalNotesStepName() {
      let text = '<span>General Notes</span>';

      if (this.generalNotes && this.currentStep > 9) {
        text += ` <span style="color: blue;"><strong>(${this.generalNotes})</strong></span>`;
      }

      return text;
    },
  },
  mounted() {
    for (let i = 1.0; i <= 16; i += 0.25) {
      this.sizeOptions.push(i);
    }

    for (let i = 1; i <= 100; i += 1) {
      this.duplicateCountOptions.push(i);
    }
  },
  methods: {
    ...mapActions('apiClient', ['create']),
    nextStep() {
      this.currentStep += 1;
    },
    previousStep() {
      this.currentStep -= 1;
    },
    resetAdditionalServices() {
      this.addlServices = false;
      this.previousStep();
    },
    resetPalletDisposal() {
      this.palletDisposal = false;
      this.previousStep();
    },
    needsClientAttention() {
      this.clientAttentionRequired = true;
    },
    resetClientAttention() {
      this.clientAttentionRequired = false;
      this.previousStep();
    },
    resetAdditionalMaterials() {
      this.addlMats = false;
      this.previousStep();
    },
    clientSelected(client) {
      this.client = client;
    },
    subClientSelected(subClient) {
      this.subClient = subClient;
    },
    mfgSelected(mfg) {
      this.mfg = mfg;
    },
    warehouseSelected(warehouse) {
      const previousWarehouseId = this.warehouse ? this.warehouse.warehouseId : null;
      this.warehouse = warehouse;
      if (!warehouse || warehouse.warehouseId !== previousWarehouseId) {
        this.warehouseLocation = null;
        this.locationSelectorKey += 1;
      }
    },
    locationSelected(location) {
      this.warehouseLocation = location;
    },
    showCreateDuplicates() {
      this.createDuplicatesDialog = true;
    },
    async createDuplicates() {
      this.creatingDuplicates = true;

      const duplicate = JSON.parse(JSON.stringify(this.item));
      duplicate.itemId = null;
      duplicate.icn = null;
      duplicate.clientAttentionRequired = false;

      for (let i = 0; i < this.duplicateCount; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await this.create({ url: '/inventory', body: duplicate }).then((response) => {
          this.createdDuplicates.push(response.data);
          if (this.firstFile !== null) {
            this.uploadingPhoto1 = true;
            this.uploadFile(this.firstFile, response.data.itemId);
            this.upload1Complete = true;
          }

          if (this.secondFile !== null) {
            this.uploadingPhoto2 = true;
            this.uploadFile(this.secondFile, response.data.itemId);
            this.upload2Complete = true;
          }

          if (this.thirdFile !== null) {
            this.uploadingPhoto3 = true;
            this.uploadFile(this.thirdFile, response.data.itemId);
            this.upload3Complete = true;
          }
        });
      }

      this.creatingDuplicates = false;
    },
    reviewItem() {
      this.invItem = {
        client: this.client,
        subClient: this.subClient,
        manufacturer: this.mfg,
        description: this.desc,
        length: this.length,
        width: this.width,
        height: this.height,
        additionalMaterials: this.selectedMaterials,
        additionalServices: this.selectedServices,
        additionalMaterialsNotes: this.addlMaterialsNotes,
        additionalServicesNotes: this.addlServicesNotes,
        receivedDate: new Date(this.date),
        generalNotes: this.generalNotes,
        clientAttentionRequired: this.clientAttentionRequired,
        palletDisposalCount: this.palletDisposalCount,
        warehouse: this.warehouse,
        warehouseLocation: this.warehouseLocation,
      };

      this.nextStep();
    },
    async uploadFile(file, itemId) {
      this.msg = 'Uploading images';

      const formData = new FormData();
      formData.append('uploadFile', file, file.name);

      await this.create({ url: `/assets/${itemId}`, body: formData }).then(
        () => {
          this.uploaded += 1;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async createItem() {
      this.createItemDialog = true;
      this.itemCreated = true;

      await this.create({ url: '/inventory', body: this.invItem }).then(
        (response) => {
          this.item = response.data;
          this.createItemDialog = true;
          this.itemCreated = true;
        },
        (error) => {
          console.log(error);
        },
      );

      if (this.firstFile !== null) {
        this.uploadingPhoto1 = true;
        await this.uploadFile(this.firstFile, this.item.itemId);
        this.upload1Complete = true;
      }

      if (this.secondFile !== null) {
        this.uploadingPhoto2 = true;
        await this.uploadFile(this.secondFile, this.item.itemId);
        this.upload2Complete = true;
      }

      if (this.thirdFile !== null) {
        this.uploadingPhoto3 = true;
        await this.uploadFile(this.thirdFile, this.item.itemId);
        this.upload3Complete = true;
      }

      if (this.billOfLading !== null) {
        this.uploadingBillOfLading = true;
        await this.uploadFile(this.billOfLading, this.item.itemId);
        this.uploadBillOfLadingComplete = true;
      }
    },
    newItem() {
      this.$router.go();
    },
    newItemInSequence() {
      this.desc = null;
      this.length = 1.0;
      this.width = 1.0;
      this.height = 1.0;
      this.selectedMaterials = 0;
      this.selectedServices = null;
      this.addlMaterialsNotes = '';
      this.addlServicesNotes = '';
      this.generalNotes = null;
      this.addGeneralNotes = false;
      this.firstFile = null;
      this.secondFile = null;
      this.thirdFile = null;
      this.addlServices = false;
      this.addlMats = false;
      this.createItemDialog = false;
      this.rerenderMfgAutocomplete += 1;
      this.clientAttentionRequired = false;
      this.palletDisposalCount = 0;
      this.palletDisposal = false;
      this.warehouseLocation = null;
      this.locationSelectorKey += 1;

      this.currentStep = this.steps.manufacturerSelect;
      this.itemCreated = false;
    },
  },
};
</script>
{}
